/*
 * The observation this device is walking to (field navigation, 2026-10-13).
 *
 * "Set as target" on the national map writes it here -- a historical
 * observation (its sample_id) or a pin dropped on the map (no id) -- and both
 * the map and the camera read it: the map draws the line from the phone's fix
 * and says how far and which way, the camera shows a mini-map over the
 * viewfinder. Every batch the camera starts while a target is set is stamped
 * with its id, so the outbox sends it as POST /collections target_sample_id
 * and the server records which observation the new record documents. A
 * dropped pin has no id to send: it only guides.
 *
 * localStorage, not the batch store, as utils/activeMission.js: one per device
 * and per person, there before the first photo of the next batch, keyed by the
 * signed-in person's id (compared as strings: a token's id and a stored one
 * may not agree on type). Unlike a mission it has no end: it stays until it is
 * cleared, from the camera's chip or the map's banner.
 *
 * A `storage` event never reaches the tab that wrote it, so a change made in
 * this tab is announced on `window` as well (CHANGE_EVENT): the map's drawer
 * sets a target and the map's banner shows it at once.
 */
import { useEffect, useState } from 'react';
import { isValidCoordinate } from './geo';

export const ACTIVE_TARGET_KEY = 'mb_active_target';
export const CHANGE_EVENT = 'pitriot:active-target';

function storageOf(storage) {
  if (storage) return storage;
  try { return globalThis.localStorage; } catch { return null; }
}

function announce() {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new Event(CHANGE_EVENT));
    }
  } catch {
    /* an environment with no events: the next read finds it anyway */
  }
}

/** The target for this person, or null: none, another person's, or junk. */
export function readActiveTarget(userId, storage) {
  const store = storageOf(storage);
  if (!store) return null;
  try {
    const raw = store.getItem(ACTIVE_TARGET_KEY);
    if (!raw) return null;
    const target = JSON.parse(raw);
    if (!target || typeof target !== 'object' || !isValidCoordinate(target.lat, target.lng)) return null;
    if (userId != null && target.userId != null && String(target.userId) !== String(userId)) return null;
    return { ...target, lat: Number(target.lat), lng: Number(target.lng), targetId: target.targetId || null,
      label: target.label || '' };
  } catch {
    return null;
  }
}

/**
 * Make this the target. `targetId` is the observation's sample_id, or null
 * for a dropped pin; `label` what to call it (taxon or code). Returns what
 * was written, or null when it could not be.
 */
export function setActiveTarget({ targetId = null, lat, lng, label = '', userId = null }, storage) {
  const store = storageOf(storage);
  if (!store || !isValidCoordinate(lat, lng)) return null;
  const target = { targetId: targetId || null, lat: Number(lat), lng: Number(lng), label: label || '',
    userId: userId == null ? null : String(userId), since: new Date().toISOString() };
  try {
    store.setItem(ACTIVE_TARGET_KEY, JSON.stringify(target));
  } catch {
    return null;
  }
  announce();
  return target;
}

export function clearActiveTarget(storage) {
  const store = storageOf(storage);
  if (!store) return;
  try {
    store.removeItem(ACTIVE_TARGET_KEY);
  } catch {
    /* nothing to clear */
  }
  announce();
}

/**
 * Whether a point on the map is the target: the same server record, or, for a
 * target with no record (a dropped pin, a batch still on this device), the
 * same spot.
 */
export function isTargetOf(target, point) {
  if (!target || !point) return false;
  if (target.targetId) return !point.local && point.id != null && String(target.targetId) === String(point.id);
  return Math.abs(Number(point.lat) - target.lat) < 1e-6 && Math.abs(Number(point.lng) - target.lng) < 1e-6;
}

/** The active target for this person, following changes in this tab and others. */
export function useActiveTarget(userId) {
  const [target, setTarget] = useState(() => readActiveTarget(userId));
  useEffect(() => {
    const refresh = () => setTarget(readActiveTarget(userId));
    refresh();
    const onStorage = (event) => {
      if (!event || event.key === null || event.key === ACTIVE_TARGET_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CHANGE_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHANGE_EVENT, refresh);
    };
  }, [userId]);
  return target;
}
